import { createContext, useContext, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Site {
  id: string;
  name: string;
  city: string;
  country: string;
}

interface SiteFilterContextType {
  selectedSiteId: string | undefined;
  setSelectedSiteId: (siteId: string | undefined) => void;
  sites: Site[];
  isLoading: boolean;
  hasSites: boolean;
}

const SiteFilterContext = createContext<SiteFilterContextType | undefined>(undefined);

export const SiteFilterProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);

  // Get user's company ID
  const { data: profile } = useQuery({
    queryKey: ["profile-for-sites"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("user_id", user.id)
        .single();
      return data;
    }
  });

  // Fetch active sites
  const { data: sites = [], isLoading } = useQuery({
    queryKey: ["sites-filter", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data, error } = await supabase
        .from("sites")
        .select("id, name, city, country")
        .eq("company_id", profile.company_id)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data as Site[];
    },
    enabled: !!profile?.company_id
  });

  return (
    <SiteFilterContext.Provider 
      value={{ 
        selectedSiteId, 
        setSelectedSiteId, 
        sites, 
        isLoading,
        hasSites: sites.length > 0
      }}
    >
      {children}
    </SiteFilterContext.Provider>
  );
};

export const useSiteFilter = () => {
  const context = useContext(SiteFilterContext);
  if (context === undefined) {
    throw new Error("useSiteFilter must be used within a SiteFilterProvider");
  }
  return context;
};
